import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "../api/axios";
import { useLocation } from "react-router-dom";

const AuthContext = createContext({});

// Helper function to get tenant_id from URL params (fallback for direct access)
const getTenantIdFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("tenant_id") || params.get("tenantId");
};

// Helper function to extract tenant_id from consent-related URL params
const getTenantIdFromConsentUrl = () => {
  const params = new URLSearchParams(window.location.search);
  // The backend should include tenantId alongside consentId in the redirect
  return params.get("tenantId") || params.get("tenant_id");
};

export const AuthProvider = ({ children }) => {
  const { pathname } = useLocation();
  // Multi-tenant auth sessions: array of { tenant_id, auth, permissions, role, created_at, updated_at }
  const [tenantSessions, setTenantSessions] = useState([]);

  // Legacy single auth for backward compatibility (will be derived from current tenant session)
  const [auth, setAuth] = useState({});
  const [persist, setPersist] = useState(
    JSON.parse(localStorage.getItem("persist")) || false
  );
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState([]);

  // Keep persist flag in localStorage in a central place so refresh-on-reload works reliably
  useEffect(() => {
    try {
      localStorage.setItem("persist", JSON.stringify(persist));
    } catch {}
  }, [persist]);

  const [redirectUri, setRedirectUri] = useState();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [userTenants, setUserTenants] = useState([]);
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);

  // Helper functions for managing multi-tenant sessions
  const addOrUpdateTenantSession = useCallback(
    (
      tenantId,
      authData,
      permissionsData = [],
      roleData = [],
      tenantMeta = {}
    ) => {
      const now = new Date().toISOString();
      const tokenIssuedAt = new Date().getTime();

      setTenantSessions((prev) => {
        const existingIndex = prev.findIndex(
          (session) => session.tenant_id === tenantId
        );
        const sessionData = {
          tenant_id: tenantId,
          auth: {
            ...authData,
            token_issued_at: tokenIssuedAt,
          },
          permissions: permissionsData,
          role: roleData,
          tenant_name:
            tenantMeta?.name ||
            (existingIndex >= 0 ? prev[existingIndex]?.tenant_name : null),
          tenant_logo:
            tenantMeta?.logo ||
            (existingIndex >= 0 ? prev[existingIndex]?.tenant_logo : null),
          updated_at: now,
          created_at: existingIndex >= 0 ? prev[existingIndex].created_at : now,
        };

        let newSessions;
        if (existingIndex >= 0) {
          newSessions = [...prev];
          newSessions[existingIndex] = sessionData;
        } else {
          newSessions = [...prev, sessionData];
        }

        // Store only tenant IDs in localStorage
        try {
          const tenantIds = newSessions.map((s) => s.tenant_id);
          localStorage.setItem("activeTenants", JSON.stringify(tenantIds));
        } catch {}

        return newSessions;
      });
    },
    []
  );

  const getTenantSession = useCallback(
    (tenantId) => {
      return (
        tenantSessions.find((session) => session.tenant_id === tenantId) || null
      );
    },
    [tenantSessions]
  );

  const removeTenantSession = (tenantId) => {
    setTenantSessions((prev) => {
      const newSessions = prev.filter(
        (session) => session.tenant_id !== tenantId
      );

      try {
        const tenantIds = newSessions.map((s) => s.tenant_id);
        localStorage.setItem("activeTenants", JSON.stringify(tenantIds));
      } catch {}

      return newSessions;
    });
  };

  const clearAllTenantSessions = () => {
    setTenantSessions([]);
    try {
      localStorage.removeItem("activeTenants");
    } catch {}
  };

  // Clear tenant_id from the URL without reloading
  const clearTenantFromUrl = () => {
    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("tenant_id");
      currentUrl.searchParams.delete("tenantId");
      window.history.replaceState({}, "", currentUrl.toString());
    } catch (e) {
      console.warn("Failed to clear tenant params from URL", e);
    }
  };

  // Clear current tenant context (and optionally URL params)
  const clearTenantContext = (clearUrlParams = true) => {
    setCurrentTenant(null);
    if (clearUrlParams) clearTenantFromUrl();
  };

  // Helper function to check if auth token is valid/not expired
  const isTokenValid = (authData) => {
    if (!authData || !authData.access_token) {
      return false;
    }

    // Check if token has expired based on issue time and expires_in
    if (authData.expires_in && authData.token_issued_at) {
      const now = new Date().getTime();
      const expirationTime =
        authData.token_issued_at + authData.expires_in * 1000; // Convert to milliseconds

      // Add a 30-second buffer to avoid edge cases
      const bufferTime = 30 * 1000;

      if (now >= expirationTime - bufferTime) {
        console.log(
          `Token expired for tenant. Issued at: ${new Date(
            authData.token_issued_at
          )}, Expires in: ${authData.expires_in}s`
        );
        return false;
      }

      return true;
    }

    // If no expiration info but has token, assume valid (backend will validate)
    // This handles cases where the token doesn't include expiration info
    return authData.access_token ? true : false;
  };

  const switchToTenant = useMemo(
    () =>
      async (tenantId, redirectToLogin = true) => {
        const session = getTenantSession(tenantId);

        if (session && isTokenValid(session.auth)) {
          // Valid session exists, switch to it
          setCurrentTenant({
            id: tenantId,
            name: session?.tenant_name || null,
            logo: session?.tenant_logo || null,
          });
          setAuth(session.auth || {});
          setPermissions(session.permissions || []);
          setRole(session.role || []);

          // Update URL to reflect tenant context
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set("tenant_id", tenantId);
          window.history.replaceState({}, "", currentUrl.toString());

          return { success: true, authenticated: true };
        } else {
          // No valid session for this tenant
          if (session) {
            // Remove invalid/expired session
            removeTenantSession(tenantId);
          }

          if (redirectToLogin) {
            // Set the tenant context and redirect to login
            setCurrentTenant({ id: tenantId });
            setAuth({});
            setPermissions([]);
            setRole([]);

            // Redirect to login with tenant context
            const loginUrl = `/login?tenant_id=${tenantId}`;
            window.location.href = loginUrl;

            return { success: true, authenticated: false, redirected: true };
          } else {
            // Just set tenant context without redirect (for programmatic use)
            setCurrentTenant({ id: tenantId });
            setAuth({});
            setPermissions([]);
            setRole([]);

            return { success: true, authenticated: false, redirected: false };
          }
        }
      },
    [getTenantSession]
  );

  // Enhanced setAuth that handles multi-tenant sessions
  const setAuthWithTenant = useCallback(
    (authData, tenantId = null) => {
      const targetTenantId =
        tenantId || currentTenant?.id || getTenantIdFromUrl();

      if (targetTenantId) {
        // Update the specific tenant session
        addOrUpdateTenantSession(
          targetTenantId,
          authData,
          permissions,
          role,
          currentTenant
        );

        // If this is the current tenant, update legacy state for backward compatibility
        if (targetTenantId === currentTenant?.id) {
          setAuth(authData);
        }
      } else {
        // Fallback to legacy behavior if no tenant context
        setAuth(authData);
      }
    },
    [
      addOrUpdateTenantSession,
      currentTenant?.id,
      currentTenant?.name,
      currentTenant?.logo,
      permissions,
      role,
    ]
  );

  // Helper function to check if user is accessing SSO directly (without tenant context)
  const isDirectSSOAccess = () => {
    const tenantId = getTenantIdFromUrl();
    return !tenantId; // No tenant_id in URL means direct access
  };

  // Helper function to determine if we need tenant selection
  const needsTenantSelection = () => {
    return isDirectSSOAccess() && !currentTenant?.id;
  };

  // Effect to sync current tenant from URL parameters and load session
  useEffect(() => {
    const tenantId = getTenantIdFromUrl();

    // Do not hydrate auth from existing sessions on auth pages
    if (
      pathname.includes("login") ||
      pathname.includes("register") ||
      pathname.includes("telegram_auth")
    ) {
      if (tenantId && tenantId !== currentTenant?.id) {
        setCurrentTenant({ id: tenantId });
      }
      return;
    }

    if (tenantId && tenantId !== currentTenant?.id) {
      setCurrentTenant({ id: tenantId });

      // Check if we have an existing session for this tenant
      const existingSession = getTenantSession(tenantId);
      if (existingSession && isTokenValid(existingSession.auth)) {
        // Load existing session
        setAuth(existingSession.auth);
        setPermissions(existingSession.permissions || []);
        setRole(existingSession.role || []);
      }
    } else if (!tenantId && currentTenant?.id) {
      // URL doesn't have tenant_id but we have a current tenant
      // This handles the direct SSO access scenario
      // Keep the current tenant context for now
      console.log(
        "Direct SSO access detected - maintaining current tenant context"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const urlTenantId = getTenantIdFromUrl();
        const lastSessionTenant = tenantSessions[tenantSessions.length - 1];

        const isAuthFlow =
          pathname.includes("consent") ||
          pathname.includes("login") ||
          pathname.includes("register");

        let tenantId =
          isAuthFlow && urlTenantId
            ? urlTenantId
            : urlTenantId || lastSessionTenant?.tenant_id;

        // If no tenant found, try to restore from localStorage metadata
        let allStoredTenantIds = [];
        if (!tenantId) {
          try {
            const stored = localStorage.getItem("activeTenants");
            allStoredTenantIds = stored ? JSON.parse(stored) : [];
            tenantId = allStoredTenantIds[allStoredTenantIds.length - 1];
          } catch {}
        } else {
          // Also get all stored tenant IDs for restoration
          try {
            const stored = localStorage.getItem("activeTenants");
            allStoredTenantIds = stored ? JSON.parse(stored) : [];
          } catch {}
        }

        if (!tenantId) {
          setIsAuthInitializing(false);
          return;
        }

        const hasExistingSession = tenantSessions.length > 0;
        const requestedTenantMatchesLastSession =
          lastSessionTenant?.tenant_id === tenantId;

        if (
          (pathname.includes("login") || pathname.includes("register")) &&
          hasExistingSession &&
          !requestedTenantMatchesLastSession
        ) {
          setAuth({});
          setIsAuthInitializing(false);
          return;
        }

        // On initial load, try to restore all tenant sessions
        if (!hasExistingSession && allStoredTenantIds.length > 0) {
          // Restore all tenant sessions in parallel
          const restorePromises = allStoredTenantIds.map(async (tid) => {
            try {
              const response = await axios.get(
                `/refreshToken?tenant_id=${tid}`,
                {
                  withCredentials: true,
                }
              );
              const authData = response?.data?.data;
              if (authData) {
                addOrUpdateTenantSession(tid, authData);
              }
              return { tenantId: tid, success: true, authData };
            } catch (error) {
              console.warn(`Failed to restore session for tenant ${tid}:`, error);
              if (error?.response?.status === 401) {
                removeTenantSession(tid);
              }
              return { tenantId: tid, success: false };
            }
          });

          const results = await Promise.all(restorePromises);
          
          // Set the current auth to the primary tenant's session
          const primaryResult = results.find((r) => r.tenantId === tenantId && r.success);
          if (primaryResult) {
            setAuth(primaryResult.authData);
          }
        } else {
          // Normal single-tenant refresh
          const response = await axios.get(
            `/refreshToken?tenant_id=${tenantId}`,
            {
              withCredentials: true,
            }
          );

          const authData = response?.data?.data;

          if (tenantId) {
            addOrUpdateTenantSession(tenantId, authData);
          }

          setAuth(authData);
        }
      } catch (error) {
        setAuth({});
        const tenantId = getTenantIdFromUrl();
        if (tenantId && error?.response?.status === 401) {
          removeTenantSession(tenantId);
        }
      } finally {
        setIsAuthInitializing(false);
      }
    };

    const skipRefresh = [
      "consent",
      "error",
      "opIframe",
      "rpLogout",
      "login",
      "register",
      "telegram_auth",
    ].some((s) => pathname.includes(s));

    if (skipRefresh) {
      setIsAuthInitializing(false);
      return;
    }

    if (auth?.access_token && isTokenValid(auth)) {
      setIsAuthInitializing(false);
      return;
    }

    getAccessToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const value = useMemo(
    () => ({
      // Legacy single-tenant auth (for backward compatibility)
      auth,
      setAuth,
      persist,
      setPersist,
      permissions,
      setPermissions,
      role,
      setRole,

      // Multi-tenant session management
      tenantSessions,
      setAuthWithTenant,
      addOrUpdateTenantSession,
      getTenantSession,
      removeTenantSession,
      clearAllTenantSessions,
      switchToTenant,

      // Navigation and tenant management
      redirectUri,
      setRedirectUri,
      currentTenant,
      setCurrentTenant,
      userTenants,
      setUserTenants,
      getTenantIdFromUrl,
      getTenantIdFromConsentUrl,

      // Direct SSO access helpers
      isDirectSSOAccess,
      needsTenantSelection,

      // Tenant helpers
      clearTenantContext,

      // Initialization/loading flags
      isAuthInitializing,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      auth,
      persist,
      permissions,
      role,
      tenantSessions,
      setAuthWithTenant,
      getTenantSession,
      switchToTenant,
      redirectUri,
      currentTenant,
      userTenants,
      needsTenantSelection,
    ]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
